<script setup lang="ts">
/**
 * 말레이시아 주소 입력 모달 (CDD 문서 주소 필드용 · 재사용).
 *
 * 챗봇 주소 위젯(ChatMalaysiaAddressInput)과 **동일하게** 구현:
 *   · 데이터 소스 = 백엔드 API (entityMyApi.getMalaysiaStates/Cities/Postcodes, baseCodeApi B15 국가)
 *   · Country 드롭다운 → Malaysia 면 State/City/Postcode cascading, 그 외는 자유입력
 *   · Postcode 콤보박스 — 타이핑 중 역조회(getMalaysiaPostcodesByNumber)로 State·City 자동 채움
 *   · 코드(stateCode/cityCode) 저장 후 이름으로 변환하여 문서 필드에 매핑
 *
 * 저장 시 { line, town, state, postcode, country } 를 emit → 부모가 문서 변수에 매핑.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';

const MALAYSIA_CODE = 'B15135';

interface AddrInit { line?: string; town?: string; state?: string; postcode?: string; country?: string; }

const props = defineProps<{
  modelValue: boolean;
  label: string;
  hasComponents: boolean; // town/state/postcode/country 형제 필드 존재 여부
  initial: AddrInit;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'apply', v: {
    line: string;
    line1?: string;
    line2?: string;
    line3?: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
  }): void;
}>();

const { t } = useI18n();

// ── Form state (챗봇과 동일한 code 기반) ─────────────────────
const form = ref({
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  countryCode: MALAYSIA_CODE as string,
  stateCode: '' as string,
  cityCode: '' as string,
  postcodeCode: '' as string,
  foreignState: '',
  foreignCity: '',
  foreignPostcode: '',
});

interface LocOption { code: string; name: string }
interface CodeOption { TOTAL_CODE: string; CODE_NAME: string }

const countryOptions = ref<CodeOption[]>([]);
const stateOptions = ref<LocOption[]>([]);
const cityOptions = ref<LocOption[]>([]);
const postcodeOptions = ref<LocOption[]>([]);

const loadingCountries = ref(false);
const loadingStates = ref(false);
const loadingCities = ref(false);
const loadingPostcodes = ref(false);

const isMalaysiaSelected = computed(() => !form.value.countryCode || form.value.countryCode === MALAYSIA_CODE);

// Postcode 콤보박스 상태 (드롭다운 선택 또는 직접 입력)
const postcodeInput = ref<string | LocOption | null>(null);
const isReverseLookup = ref(false); // 역조회 중 cascading watch 방지
let postcodeSearchDebounce: ReturnType<typeof setTimeout> | null = null;
let statesReady: Promise<void> | null = null;

// ── Country + States 로드 (mount 시 1회) ────────────────────
onMounted(() => {
  loadingCountries.value = true;
  baseCodeApi.getCodesByMainCode('CM', 'B15').then((res: any) => {
    const codes = res?.codes || res?.data?.codes || [];
    countryOptions.value = codes
      .map((c: any) => ({
        TOTAL_CODE: String(c.totalCode || c.TOTAL_CODE || '').trim(),
        CODE_NAME: String(c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '').trim().toUpperCase(),
      }))
      .filter((c: any) => c.TOTAL_CODE && c.CODE_NAME && !c.TOTAL_CODE.endsWith('000'));
  }).catch((err) => console.error('[CddAddress] load countries failed:', err))
    .finally(() => { loadingCountries.value = false; });

  loadingStates.value = true;
  statesReady = entityMyApi.getMalaysiaStates().then((res: any) => {
    const data = res?.data?.data || res?.data || [];
    stateOptions.value = Array.isArray(data) ? data : [];
  }).catch((err) => { console.error('[CddAddress] load states failed:', err); stateOptions.value = []; })
    .finally(() => { loadingStates.value = false; });
});

// ── Country 변경 → MY 종속 필드 리셋 ────────────────────────
watch(() => form.value.countryCode, (newC, oldC) => {
  if (newC === oldC) return;
  form.value.stateCode = ''; form.value.cityCode = ''; form.value.postcodeCode = '';
  form.value.foreignState = ''; form.value.foreignCity = ''; form.value.foreignPostcode = '';
  postcodeInput.value = null;
  cityOptions.value = []; postcodeOptions.value = [];
});

// ── Cascading: State → City ─────────────────────────────────
watch(() => form.value.stateCode, async (newState) => {
  if (isReverseLookup.value) return;
  form.value.cityCode = ''; form.value.postcodeCode = ''; postcodeInput.value = null;
  cityOptions.value = []; postcodeOptions.value = [];
  if (!newState) return;
  loadingCities.value = true;
  try {
    const res: any = await entityMyApi.getMalaysiaCities(newState);
    const data = res?.data?.data || res?.data || [];
    cityOptions.value = Array.isArray(data) ? data : [];
  } catch (err) { console.error('[CddAddress] load cities failed:', err); cityOptions.value = []; }
  finally { loadingCities.value = false; }
});

// ── Cascading: City → Postcode ──────────────────────────────
watch(() => form.value.cityCode, async (newCity) => {
  if (isReverseLookup.value) return;
  form.value.postcodeCode = ''; postcodeInput.value = null; postcodeOptions.value = [];
  if (!newCity) return;
  loadingPostcodes.value = true;
  try {
    const res: any = await entityMyApi.getMalaysiaPostcodes(newCity);
    const data = res?.data?.data || res?.data || [];
    postcodeOptions.value = Array.isArray(data) ? data : [];
  } catch (err) { console.error('[CddAddress] load postcodes failed:', err); postcodeOptions.value = []; }
  finally { loadingPostcodes.value = false; }
});

// ── Postcode 콤보박스 → postcodeCode 동기화 ──────────────────
watch(postcodeInput, (val) => {
  if (val && typeof val === 'object' && 'code' in val) form.value.postcodeCode = val.code;
  else if (typeof val === 'string') form.value.postcodeCode = val;
  else form.value.postcodeCode = '';
});

// ── Postcode 직접 입력 → 역조회 (State/City 자동 채움) ────────
function onPostcodeSearch(searchText: string) {
  if (postcodeSearchDebounce) clearTimeout(postcodeSearchDebounce);
  if (!searchText || searchText.length < 4) return;
  postcodeSearchDebounce = setTimeout(async () => {
    if (form.value.stateCode && form.value.cityCode) return; // 이미 선택됨
    try {
      loadingPostcodes.value = true;
      const res: any = await entityMyApi.getMalaysiaPostcodesByNumber(searchText);
      const data = res?.data?.data || res?.data || [];
      if (!Array.isArray(data) || data.length === 0) return;
      const match = data[0];
      const stateCode = match.state?.code;
      const cityCode = match.city?.code;
      if (!stateCode) return;
      isReverseLookup.value = true;
      if (form.value.countryCode !== MALAYSIA_CODE) form.value.countryCode = MALAYSIA_CODE;
      form.value.stateCode = stateCode;
      const cityRes: any = await entityMyApi.getMalaysiaCities(stateCode);
      cityOptions.value = Array.isArray(cityRes?.data?.data || cityRes?.data) ? (cityRes?.data?.data || cityRes?.data) : [];
      if (cityCode) {
        form.value.cityCode = cityCode;
        const pcRes: any = await entityMyApi.getMalaysiaPostcodes(cityCode);
        postcodeOptions.value = Array.isArray(pcRes?.data?.data || pcRes?.data) ? (pcRes?.data?.data || pcRes?.data) : [];
      }
      const pcOption = postcodeOptions.value.find((o) => o.name === searchText || o.code === searchText);
      if (pcOption) { postcodeInput.value = pcOption.code; form.value.postcodeCode = pcOption.code; }
      else form.value.postcodeCode = searchText;
      isReverseLookup.value = false;
    } catch (err) { console.error('[CddAddress] postcode reverse lookup failed:', err); isReverseLookup.value = false; }
    finally { loadingPostcodes.value = false; }
  }, 500);
}

// ── 이름 변환 (문서 필드 출력용) ─────────────────────────────
const countryName = computed(() => countryOptions.value.find((o) => o.TOTAL_CODE === form.value.countryCode)?.CODE_NAME || (isMalaysiaSelected.value ? 'MALAYSIA' : ''));
const stateName = computed(() => (isMalaysiaSelected.value ? stateOptions.value.find((o) => o.code === form.value.stateCode)?.name || '' : form.value.foreignState.trim()));
const cityName = computed(() => (isMalaysiaSelected.value ? cityOptions.value.find((o) => o.code === form.value.cityCode)?.name || '' : form.value.foreignCity.trim()));
const postcodeName = computed(() => {
  if (isMalaysiaSelected.value) return postcodeOptions.value.find((o) => o.code === form.value.postcodeCode)?.name || form.value.postcodeCode || '';
  return form.value.foreignPostcode.trim();
});

const linesJoined = computed(() =>
  [form.value.addressLine1, form.value.addressLine2, form.value.addressLine3]
    .map((x) => x.trim())
    .filter(Boolean)
    .join(', ')
    .toUpperCase(),
);
const formattedAddress = computed(() => {
  const parts: string[] = [];
  if (linesJoined.value) parts.push(...[form.value.addressLine1, form.value.addressLine2, form.value.addressLine3].map((x) => x.trim()).filter(Boolean));
  const pc = postcodeName.value; const ct = cityName.value;
  if (pc && ct) parts.push(`${pc} ${ct}`); else if (ct) parts.push(ct); else if (pc) parts.push(pc);
  if (stateName.value) parts.push(stateName.value);
  const cn = countryName.value;
  if (cn && parts.length) parts.push(cn);
  return parts.map((p) => p.trim()).filter(Boolean).join(', ').toUpperCase();
});

// ── 열릴 때 initial 로 best-effort prefill ──────────────────
async function reset() {
  const i = props.initial || {};
  form.value = {
    addressLine1: i.line || '', addressLine2: '', addressLine3: '',
    countryCode: MALAYSIA_CODE, stateCode: '', cityCode: '', postcodeCode: '',
    foreignState: '', foreignCity: '', foreignPostcode: '',
  };
  postcodeInput.value = null;

  const foreign = !!(i.country && !/malays/i.test(i.country));
  if (foreign) {
    // 국가명 → 코드 매칭 (로드 후)
    const setForeign = () => {
      const hit = countryOptions.value.find((o) => o.CODE_NAME.toUpperCase() === String(i.country).toUpperCase());
      form.value.countryCode = hit?.TOTAL_CODE || (countryOptions.value[0]?.TOTAL_CODE ?? '');
      form.value.foreignState = i.state || ''; form.value.foreignCity = i.town || ''; form.value.foreignPostcode = i.postcode || '';
    };
    if (countryOptions.value.length) setForeign(); else setTimeout(setForeign, 400);
    return;
  }
  // Malaysia: postcode 있으면 역조회로 state/city 채움, 없으면 state 이름 매칭
  await (statesReady || Promise.resolve());
  if (i.postcode) {
    onPostcodeSearch(i.postcode);
  } else if (i.state) {
    const st = stateOptions.value.find((o) => o.name.toUpperCase() === String(i.state).toUpperCase());
    if (st) form.value.stateCode = st.code;
  }
}
watch(() => props.modelValue, (open) => { if (open) reset(); });

function close() { emit('update:modelValue', false); }
const canApply = computed(() => linesJoined.value.length > 0 || stateName.value.length > 0 || postcodeName.value.length > 0);
function apply() {
  const up = (s: string) => String(s || '').trim().toUpperCase();
  emit('apply', {
    line: up(props.hasComponents ? linesJoined.value : formattedAddress.value),
    ...(props.hasComponents
      ? {
          line1: up(form.value.addressLine1),
          line2: up(form.value.addressLine2),
          line3: up(form.value.addressLine3),
        }
      : {}),
    town: up(cityName.value),
    state: up(stateName.value),
    postcode: up(postcodeName.value),
    country: up(countryName.value),
  });
  close();
}
</script>

<template>
  <v-dialog :model-value="modelValue" max-width="560" @update:model-value="emit('update:modelValue', $event)">
    <v-card class="addr-modal">
      <div class="am-head">
        <v-icon size="18" color="primary">mdi-map-marker-outline</v-icon>
        <span class="am-title">{{ t('cdd.addrTitle') }}</span>
        <span class="am-field">{{ label }}</span>
        <v-btn icon="mdi-close" size="x-small" variant="text" class="ml-auto" @click="close" />
      </div>

      <div class="am-body">
        <!-- Address Lines -->
        <v-text-field v-model="form.addressLine1" :label="t('cdd.addrLine1')" variant="outlined" density="compact" hide-details class="mb-2" />
        <v-text-field v-model="form.addressLine2" :label="t('cdd.addrLine2')" variant="outlined" density="compact" hide-details class="mb-2" />
        <v-text-field v-model="form.addressLine3" :label="t('cdd.addrLine3')" variant="outlined" density="compact" hide-details class="mb-2" />

        <!-- Country + State -->
        <div class="am-row">
          <v-autocomplete
            v-model="form.countryCode" :items="countryOptions" item-title="CODE_NAME" item-value="TOTAL_CODE"
            :label="t('cdd.addrCountry')" placeholder="Search…" variant="outlined" density="compact"
            hide-details clearable auto-select-first :loading="loadingCountries"
          />
          <v-autocomplete
            v-if="isMalaysiaSelected" v-model="form.stateCode" :items="stateOptions" item-title="name" item-value="code"
            :label="t('cdd.addrState')" placeholder="Search…" variant="outlined" density="compact"
            hide-details clearable auto-select-first :loading="loadingStates"
          />
          <v-text-field v-else v-model="form.foreignState" :label="t('cdd.addrState')" variant="outlined" density="compact" hide-details />
        </div>

        <!-- City + Postcode -->
        <div class="am-row mt-2">
          <v-autocomplete
            v-if="isMalaysiaSelected" v-model="form.cityCode" :items="cityOptions" item-title="name" item-value="code"
            :label="t('cdd.addrCity')" placeholder="Search…" variant="outlined" density="compact"
            hide-details clearable auto-select-first :loading="loadingCities" :disabled="!form.stateCode"
          />
          <v-text-field v-else v-model="form.foreignCity" :label="t('cdd.addrCity')" variant="outlined" density="compact" hide-details />
          <v-combobox
            v-if="isMalaysiaSelected" v-model="postcodeInput" :items="postcodeOptions" item-title="name" item-value="code"
            :label="t('cdd.addrPostcode')" placeholder="Type or select…" variant="outlined" density="compact"
            hide-details clearable auto-select-first :loading="loadingPostcodes" :return-object="false"
            @update:search="onPostcodeSearch"
          />
          <v-text-field v-else v-model="form.foreignPostcode" :label="t('cdd.addrPostcode')" variant="outlined" density="compact" hide-details />
        </div>
      </div>

      <div class="am-foot">
        <v-btn variant="text" class="text-none" @click="close">{{ t('cdd.back') }}</v-btn>
        <v-btn color="primary" variant="flat" class="text-none" prepend-icon="mdi-check" :disabled="!canApply" @click="apply">{{ t('cdd.addrApply') }}</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.addr-modal { border-radius:12px; overflow:hidden; }
.am-head { display:flex; align-items:center; gap:8px; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.am-title { font-weight:700; font-size:14px; }
.am-field { font-size:11px; color:#1B84FF; background:#eaf3ff; border-radius:5px; padding:1px 7px; }
.am-body { padding:16px; }
.am-row { display:flex; gap:10px; }
.am-row > * { flex:1; }
.am-foot { display:flex; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; }
</style>
